import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { PhilosophyStrip } from "@/components/PhilosophyStrip";
import { pillars } from "@/data/pillars";

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo
        title="Services"
        path="/services"
        description="Slatech Solutions delivers three core offerings: Welding & Process Improvements (welding automation, fume extraction, post-weld cleaning), Equipment Sales & Project Development (capital equipment, lasers, cutting & bending, press brakes), and Specialized Industrial Sourcing (hard-to-find and niche industrial products)."
      />
      {/* HERO */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1612438137269-70c848e102e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
            alt="Pipe welding and heavy fabrication"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-blue-500 mb-5">What We Do</p>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight text-white leading-[0.9] max-w-4xl">
              Three Ways We Help
            </h1>
            <p className="mt-6 text-lg text-zinc-300 max-w-2xl leading-relaxed">
              Slatech Solutions focuses on three core areas — improving how industrial work gets done,
              brokering the equipment that makes it happen, and sourcing the products that are hard to find.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section data-testid="services-list" className="bg-background py-16 md:py-24 bg-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-px bg-white/10 border border-white/10">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            const reverse = i % 2 === 1;
            return (
              <Reveal
                key={p.id}
                data-testid={`pillar-${p.id}`}
                className={`grid grid-cols-1 lg:grid-cols-2 bg-zinc-950 group`}
              >
                <div className={`relative min-h-[300px] overflow-hidden ${reverse ? "lg:order-2" : ""}`}>
                  <img
                    src={p.image}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <span className="text-blue-500 mb-5"><Icon size={40} strokeWidth={1.5} /></span>
                  <h3 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-zinc-400 mt-4 text-lg leading-relaxed">{p.description}</p>
                  <ul className="mt-6 space-y-3">
                    {p.items.map((item) => (
                      <li key={item} className="flex items-center gap-3 text-zinc-200">
                        <span className="text-blue-500 shrink-0"><Check size={18} strokeWidth={2.5} /></span>
                        <span className="font-heading uppercase tracking-wide text-base">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <PhilosophyStrip />

      {/* CTA */}
      <section className="bg-background py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
              Have a challenge in mind?
            </h2>
            <p className="mt-6 text-zinc-300 text-lg">No pressure. No obligation. Just a conversation.</p>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="mt-10 inline-flex items-center justify-center gap-2.5 font-heading text-base font-semibold uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 transition-colors duration-200 group"
            >
              Get In Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
